import { AlertCircle } from 'lucide-react';
import {
  ApiConfigSection,
  CourseDetailsSection,
  ContactInfoSection,
  FacilitiesSection,
  ImageManagementSection,
  BreadcrumbNavigation,
  FormProps,
  BC_LOCATIONS
} from './golf-course-form';
import { FormStateManager } from './golf-course-form/FormStateManager';
import { DialogManager } from './golf-course-form/DialogManager';

function GolfCourseForm({ course, onSuccess, onCancel }: FormProps) {
  return (
    <FormStateManager course={course}>
      {(state, actions) => (
        <div className="max-w-4xl mx-auto">
          <BreadcrumbNavigation
            courseName={state.formData.name}
            hasUnsavedChanges={state.hasUnsavedChanges}
            isLoading={state.isLoading}
            isReordering={state.isReordering}
            isEditing={state.isEditing}
            onBack={() => {
              if (state.hasUnsavedChanges) {
                actions.setShowExitDialog(true);
              } else {
                onCancel();
              }
            }}
            onUpdateCourse={() => {
              // Create a synthetic form event for handleSubmit
              const syntheticEvent = {
                preventDefault: () => {}
              } as React.FormEvent;
              actions.handleSubmit(syntheticEvent, onSuccess);
            }}
          />
          <div className="bg-white rounded-lg shadow-sm border mt-6 mx-6">

            <form onSubmit={(e) => actions.handleSubmit(e, onSuccess)} className="p-6">
              <div className="space-y-6">
                {/* Basic Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Course Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={state.formData.name}
                      onChange={(e) => actions.setFormData(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="Enter course name"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Location <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={state.formData.location}
                      onChange={(e) => actions.setFormData(prev => ({ ...prev, location: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      required
                    >
                      <option value="">Select location</option>
                      {BC_LOCATIONS.map(location => (
                        <option key={location} value={location}>{location}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={state.formData.description}
                    onChange={(e) => actions.setFormData(prev => ({ ...prev, description: e.target.value }))}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="Enter course description (optional)"
                  />
                </div>

                {/* Course Details */}
                <CourseDetailsSection
                  formData={state.formData}
                  setFormData={actions.setFormData}
                  isEditing={state.isEditing}
                  error={state.error}
                  setError={actions.setError}
                />

                {/* Contact Information */}
                <div className="bg-gray-50 rounded-lg p-6">
                  <ContactInfoSection
                    formData={state.formData}
                    setFormData={actions.setFormData}
                    isEditing={state.isEditing}
                    error={state.error}
                    setError={actions.setError}
                  />

                  {/* Facilities & Features */}
                  <FacilitiesSection
                    formData={state.formData}
                    setFormData={actions.setFormData}
                    isEditing={state.isEditing}
                    error={state.error}
                    setError={actions.setError}
                  />
                </div>

                {/* Booking Configuration */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Booking URL
                    </label>
                    <input
                      type="url"
                      value={state.formData.booking_url || ''}
                      onChange={(e) => actions.setFormData(prev => ({ ...prev, booking_url: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="https://example.com/booking"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      URL where users can book tee times for this course
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Booking Advance Days
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="90"
                        value={state.formData.booking_advance_days}
                        onChange={(e) => actions.setFormData(prev => ({ ...prev, booking_advance_days: parseInt(e.target.value) }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Daily Booking Open Time (UTC)
                      </label>
                      <input
                        type="time"
                        value={state.formData.booking_open_time}
                        onChange={(e) => actions.setFormData(prev => ({ ...prev, booking_open_time: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>

                {/* API Configuration */}
                <ApiConfigSection
                  formData={state.formData}
                  setFormData={actions.setFormData}
                  isEditing={state.isEditing}
                  error={state.error}
                  setError={actions.setError}
                  validationResult={state.validationResult}
                  setValidationResult={actions.setValidationResult}
                  showApiTypeDialog={state.showApiTypeDialog}
                  setShowApiTypeDialog={actions.setShowApiTypeDialog}
                  pendingApiType={state.pendingApiType}
                  handleApiTypeChange={actions.handleApiTypeChange}
                />

                {/* Image Management - Only show in edit mode */}
                {state.isEditing && (
                  <ImageManagementSection
                    course={course}
                    existingImages={state.existingImages}
                    setExistingImages={actions.setExistingImages}
                    uploadingImages={state.uploadingImages}
                    setUploadingImages={actions.setUploadingImages}
                    deletingImageIds={state.deletingImageIds}
                    setDeletingImageIds={actions.setDeletingImageIds}
                    isReordering={state.isReordering}
                    setIsReordering={actions.setIsReordering}
                    error={state.error}
                    setError={actions.setError}
                  />
                )}

                {/* Error Message */}
                {state.error && (
                  <div className="flex items-center space-x-2 text-red-600 bg-red-50 p-3 rounded-md">
                    <AlertCircle className="w-5 h-5" />
                    <span className="text-sm">{state.error}</span>
                  </div>
                )}

              </div>
            </form>
          </div>
          
          {/* Dialog Manager handles all dialogs and keyboard shortcuts */}
          <DialogManager 
            state={state}
            actions={actions}
            onCancel={onCancel}
            onSuccess={onSuccess}
          />
        </div>
      )}
    </FormStateManager>
  );
}

export default GolfCourseForm;